/* @section: layout */
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, MapPin, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL, orgInfo, navLinks, partners } from "@/data";

/* @section: header */
export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/95 border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
      style={{ backdropFilter: scrolled ? "blur(12px)" : "none" }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="FLYN Logo"
              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-contain bg-white p-0.5 shadow-sm"
            />
            <div className="hidden sm:block">
              <p className="font-bold text-sm md:text-base leading-tight text-foreground group-hover:text-primary transition-colors">
                Future Light Youth Network
              </p>
              <p className="text-xs text-muted-foreground font-mono">FLYN</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/70 hover:text-foreground hover:bg-muted"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="mailto:futurelightyouthnetwork.pdy@gmail.com"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-all duration-200"
              style={{ boxShadow: "0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)" }}
            >
              <Mail className="w-4 h-4" />
              Partner With Us
            </a>
          </div>

          {/* Mobile menu btn */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-t border-border bg-background/98"
            style={{ backdropFilter: "blur(12px)" }}
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    location.pathname === link.path
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <span>{link.label}</span>
                </Link>
              ))}
              <a
                href="mailto:info@flyn-mm.org"
                className="mt-2 flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
              >
                <Mail className="w-4 h-4" />
                Contact Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* @section: footer */
export function Footer() {
  return (
    <footer style={{ background: "linear-gradient(135deg, oklch(0.22 0.12 230), oklch(0.18 0.10 238))" }} className="mt-20">
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={LOGO_URL}
                alt="FLYN Logo"
                className="w-12 h-12 rounded-full object-contain bg-white p-1"
              />
              <div>
                <p className="font-bold text-background text-base">Future Light Youth Network</p>
                <p className="text-xs font-mono text-background/50">FLYN</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-background/60 max-w-sm">
              A youth-led civil society organization committed to empowering communities and building resilient futures in Southern Shan State, Myanmar.
            </p>
            <p className="text-xs mt-3 text-background/40 font-sans" style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}>
              တောင်ရှမ်းပြည်နယ်တွင် လူ့အဖွဲ့အစည်းများကို ခွန်အားပေး၍ ခိုင်မာသောအနာဂတ်ကို တည်ဆောက်ရန် ကတိခံပြုသော လူငယ်ဦးဆောင်သော CSO
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-background font-semibold mb-4 text-sm">Quick Links</h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-sm text-background/60 hover:text-background transition-colors"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-background font-semibold mb-4 text-sm">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-background/60">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
                <span>{orgInfo.address}</span>
              </li>
              <li>
                <a
                  href={`mailto:${orgInfo.email}`}
                  className="flex items-center gap-2 text-sm text-background/60 hover:text-background transition-colors"
                >
                  <Mail className="w-4 h-4 shrink-0 text-accent" />
                  <span className="break-all">{orgInfo.email}</span>
                </a>
              </li>
            </ul>
            <div className="mt-4">
              <p className="text-xs text-background/40 mb-1">Our Partners</p>
              <div className="flex flex-wrap gap-1">
                {partners.slice(0, 4).map((p) => (
                  <span
                    key={p.name}
                    className="text-xs px-2 py-0.5 rounded-full border border-background/20 text-background/50"
                  >
                    {p.name.split(" ")[0]}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-background/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-background/40">
            © {new Date().getFullYear()} Future Light Youth Network (FLYN). All rights reserved.
          </p>
          <p className="text-xs text-background/30">
            Est. {orgInfo.founded} · Southern Shan State, Myanmar
          </p>
        </div>
      </div>
    </footer>
  );
}

/* @section: page-layout */
interface PageLayoutProps {
  children: React.ReactNode;
}

export default function PageLayout({ children }: PageLayoutProps) {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
    </div>
  );
}
